'use client'

import React from 'react'
import { Heart } from 'lucide-react'
import { useWishlist } from '@/providers/WishlistProvider'
import { cn } from '@/utilities/cn'
import { toast } from 'sonner'
import { useRouter } from 'next/navigation'
import { useAuth } from '@/providers/Auth'
import { useLocale } from 'next-intl'
import { Button } from '@/components/ui/button'

interface WishlistButtonProps {
  productId: string
  variant?: 'icon' | 'button'
  className?: string
  isLoggedIn?: boolean // keeping as optional for backwards compatibility
  loginHref?: string // keeping as optional for backwards compatibility
}

export function WishlistButton({
  productId,
  variant = 'icon',
  className,
  isLoggedIn: propIsLoggedIn,
  loginHref: propLoginHref,
}: WishlistButtonProps) {
  const { toggle, isWishlisted, isPending } = useWishlist()
  const wishlisted = isWishlisted(productId)
  const router = useRouter()
  const { user } = useAuth()
  const locale = useLocale()
  
  const isLoggedIn = propIsLoggedIn ?? Boolean(user)
  const loginHref = propLoginHref ?? `/${locale}/login`

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (!isLoggedIn) {
      toast('Please log in to use the wishlist', {
        action: { label: 'Log in', onClick: () => router.push(loginHref) },
      })
      return
    }

    await toggle(productId)
  }

  if (variant === 'button') {
    return (
      <div className={cn('relative group/wishlist', className)}>
        <Button
          type="button"
          variant="neon-white"
          onClick={handleClick}
          disabled={isPending}
          aria-label={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
          className="font-mono uppercase tracking-wider rounded-lg dark:bg-black dark:text-[var(--neon-orange)] dark:border-white/10 w-10 h-10 flex-shrink-0 flex items-center justify-center !p-0"
        >
          <Heart
            size={18}
            className={cn(
              wishlisted
                ? 'stroke-rose-500 fill-rose-500'
                : 'stroke-rose-400 fill-transparent',
            )}
          />
        </Button>

        {/* Tooltip */}
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-black text-white text-xs font-mono rounded opacity-0 group-hover/wishlist:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10 dark:bg-white dark:text-black">
          {wishlisted ? 'Saved' : 'Save'}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-[5px] border-transparent border-t-black dark:border-t-white"></div>
        </div>
      </div>
    )
  }

  // icon variant (product cards)
  return (
    <button
      onClick={handleClick}
      disabled={isPending}
      aria-label={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
      className={cn(
        'absolute right-2 top-2 z-10 rounded-full bg-white/90 p-1.5 shadow-sm transition-colors',
        'hover:bg-white',
        wishlisted ? 'text-rose-500' : 'text-muted-foreground hover:text-rose-400',
        isPending && 'opacity-60',
        className,
      )}
    >
      <Heart
        size={16}
        className={cn(wishlisted ? 'fill-rose-500 stroke-rose-500' : 'stroke-current')}
      />
    </button>
  )
}